import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarClock, MapPin, Search, Store } from "lucide-react";
import { api, fileUrl, hexToHsl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PublicMobileMenu from "@/components/PublicMobileMenu";

function BrandMark({ business }) {
  const style = hexToHsl(business.theme_color)
    ? { "--primary": hexToHsl(business.theme_color) }
    : {};

  if (business.logo_url) {
    return (
      <img
        src={fileUrl(business.logo_url)}
        alt={`${business.name} logosu`}
        className={
          "h-14 w-14 shrink-0 rounded-xl border border-border bg-card object-contain p-1"
        }
      />
    );
  }

  return (
    <div
      className={
        "flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary "
        + "text-primary-foreground"
      }
      style={style}
      aria-hidden="true"
    >
      <Store className="h-6 w-6" />
    </div>
  );
}

function BusinessesHeader() {
  return (
    <header
      className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl"
    >
      <div
        className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5"
      >
        <div className="flex items-center gap-1.5">
          <PublicMobileMenu />
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="businesses-brand-link"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <CalendarClock className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <span className="font-display block text-xl font-bold leading-tight">
                365Rez
              </span>
              <span className="block text-[10px] leading-tight text-muted-foreground">
                İşletmeler
              </span>
            </div>
          </Link>
        </div>
        <Link to="/kayit" data-testid="businesses-register-link">
          <Button className="rounded-full" data-testid="businesses-register-button">
            İşletmeni Ekle
          </Button>
        </Link>
      </div>
    </header>
  );
}

export default function Businesses() {
  const { data: businesses = [], isLoading, isError } = useQuery({
    queryKey: ["public-businesses"],
    queryFn: () => api.get("/public/businesses").then((response) => response.data),
  });

  return (
    <div className="min-h-screen grain-bg">
      <BusinessesHeader />
      <main className="mx-auto max-w-6xl px-5 pb-20 pt-14 sm:pt-20">
        <div className="max-w-2xl">
          <div
            className={
              "mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card "
              + "px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-primary"
            }
          >
            <Search className="h-3.5 w-3.5" />
            Online randevu al
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            İşletmeleri keşfedin
          </h1>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            365Rez ile online randevu kabul eden işletmeyi seçin, uygun zamanı hemen ayırtın.
          </p>
        </div>

        {isLoading && (
          <div
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="businesses-loading-state"
          >
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className="h-52 animate-pulse rounded-2xl border border-border bg-card"
              />
            ))}
          </div>
        )}

        {isError && (
          <div
            className="mt-12 rounded-2xl border border-destructive/30 bg-card p-8 text-center"
            data-testid="businesses-error-state"
          >
            <h2 className="text-xl font-bold">İşletmeler şu an yüklenemedi</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Lütfen biraz sonra tekrar deneyin.
            </p>
          </div>
        )}

        {!isLoading && !isError && businesses.length === 0 && (
          <div
            className="mt-12 rounded-2xl border border-border bg-card p-10 text-center"
            data-testid="businesses-empty-state"
          >
            <Store className="mx-auto h-10 w-10 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-bold">Henüz listelenecek işletme yok</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              İşletmenizle online randevu almaya başlamak için hemen katılabilirsiniz.
            </p>
            <Link
              to="/kayit"
              className="mt-6 inline-block"
              data-testid="businesses-empty-register-link"
            >
              <Button data-testid="businesses-empty-register-button">İşletmeni Ekle</Button>
            </Link>
          </div>
        )}

        {!isLoading && !isError && businesses.length > 0 && (
          <div
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="businesses-list"
          >
            {businesses.map((business) => (
              <article
                key={business.slug}
                className={
                  "flex min-h-56 flex-col rounded-2xl border border-border bg-card p-6 shadow-sm "
                  + "transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
                }
                data-testid={`business-card-${business.slug}`}
              >
                <div className="flex items-start gap-4">
                  <BrandMark business={business} />
                  <div className="min-w-0">
                    <h2 className="break-words text-xl font-bold">{business.name}</h2>
                    {business.address ? (
                      <p className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                        <span className="break-words">{business.address}</span>
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Online randevu kabul ediyor
                      </p>
                    )}
                  </div>
                </div>
                <p className="mt-auto pt-7 text-sm text-muted-foreground">
                  {business.service_count} aktif hizmet
                </p>
                <Link
                  to={`/r/${business.slug}`}
                  className="mt-4"
                  data-testid={`business-booking-link-${business.slug}`}
                >
                  <Button
                    className="group w-full"
                    data-testid={`business-booking-button-${business.slug}`}
                  >
                    Rezervasyon Sayfasına Git
                    <ArrowRight
                      className={
                        "ml-2 h-4 w-4 transition-transform duration-200 "
                        + "group-hover:translate-x-1"
                      }
                    />
                  </Button>
                </Link>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}